import { selectCities, selectCountries, selectStates } from "../store/general/selectors";
import { cityInit, countryInit, countryStateInit } from "../store/general/constants";
import { City, Country, CountryState, ListItem } from "../store/general/types";
import { InterviewQuestionForEdit, PostJobStep } from "../store/workspace/types";
import { DayValue } from "@amir04lm26/react-modern-calendar-date-picker";
import { fromDatePickerFormat } from "../utils/dateHelpers";
import { useDispatch, useSelector } from "../store/store";
import { ChangeEvent, useEffect, useState } from "react";
import { setNotification } from "../store/general/slice";
import { selectUserId } from "../store/auth/selectors";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    setPostJobStep,
    setPlansModalShow,
    setContactAdminModal,
} from "../store/workspace/slice";
import {
    getJobDescriptionFromAIThunk,
    getRecruitingInterviewQuestionsThunk,
    editJobThunk,
} from "../store/workspace/thunks";
import {
    selectProfile,
    selectPostJobStep,
    selectPostingNewJob,
    selectExtractFileTextErrorModal,
    selectRecruitingInterviewQuestions,
    selectSubmittingJobModal,
} from "../store/workspace/selectors";

export type Step =
    'JobDetail' |
    'RecruitingInterview' |
    'MultipleChoiceExamDetail' |
    'CodingExamDetail' |
    'ExamDeadline' |
    'Review' |
    'Approval';

export const useEditJob = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = useSelector(selectUserId)
    const profile = useSelector(selectProfile);
    const cities = useSelector(selectCities);
    const states = useSelector(selectStates);
    const [step, setStep] = useState<Step>('JobDetail');
    const countries = useSelector(selectCountries);
    const posting = useSelector(selectPostingNewJob);
    const [stepCount, setStepCount] = useState(1);
    const [jobTitle, setJobTitle] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [city, setCity] = useState<City>(cityInit);
    const [salaryAmount, setSalaryAmount] = useState('');
    const [deadline, setDeadline] = useState<DayValue>();
    const [jobDescription, setJobDescription] = useState('');
    const [country, setCountry] = useState<Country>(countryInit);
    const [jobType, setJobType] = useState<ListItem | null>(null);
    const [takingMultipleChoiceExam, setTakingMultipleChoiceExam] = useState(true);
    const [state, setState] = useState<CountryState>(countryStateInit);
    const [workplaceType, setWorkplaceType] = useState<ListItem | null>(null);
    const [fetchingJobDescription, setFetchingJobDescription] = useState(false);
    const [salaryFrequency, setSalaryFrequency] = useState<ListItem | null>(null);
    const [codingExamLanguages, setCodingExamLanguages] = useState<ListItem[]>([]);
    const [multipleChoiceCodingExam, setMultipleChoiceCodingExam] = useState(false);
    const [multipleChoiceExamSkills, setMultipleChoiceExamSkills] = useState<string[]>(['']);
    const [multipleChoiceCodingExamLanguages, setMultipleChoiceCodingExamLanguages] = useState(['']);
    const rawInterviewQuestions = useSelector(selectRecruitingInterviewQuestions);
    const [defaultInterviewQuestions, setDefaultInterviewQuestions] = useState<InterviewQuestionForEdit[]>([]);
    const [selectedInterviewQuestions, setSelectedInterviewQuestions] = useState<InterviewQuestionForEdit[]>([]);
    const [takingRecruitingInterview, setTakingRecruitingInterview] = useState(true);
    const [resumeMatching, setResumeMatching] = useState(true);
    const extractFileTextErrorModal = useSelector(selectExtractFileTextErrorModal);
    const submittingJobModal = useSelector(selectSubmittingJobModal);

    const [isLoading, setIsLoading] = useState(false);


    // new fields
    const [searchParams] = useSearchParams();
    const jobId = searchParams.get('id');

    // ++++++

    useEffect(() => {
        dispatch(getRecruitingInterviewQuestionsThunk())
        return () => {
            dispatch(setPostJobStep('JobDetail'));
        }
    }, []);

    useEffect(() => {
        if (!rawInterviewQuestions) return;
        const questions = rawInterviewQuestions.map((i) => ({
            id: i.id,
            question: i.title,
            domain: 'general',
            answerTime: '300',
        }));

        setDefaultInterviewQuestions(questions);
    }, [rawInterviewQuestions]);

    useEffect(() => {
        switch (step) {
            case 'JobDetail':
                setStepCount(1);
                break;
            case 'RecruitingInterview':
                setStepCount(2);
                break;
            case 'MultipleChoiceExamDetail':
                setStepCount(3);
                break;
            case 'CodingExamDetail':
                setStepCount(4);
                break;
            case 'ExamDeadline':
                setStepCount(5);
                break;
            case 'Review':
                setStepCount(6);
                break;
        }
    }, [step]);

    const setCityAndState = (city: City) => {
        setCity(city);
        const state = states.find(state => `${state.id}` === city.state_id);
        state && setState(state);
        setCountry({
            id: 228,
            code: 'US',
            title: 'United States'
        });
    };

    const fileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const newList = [...files, e.target.files[0]];
        setFiles(newList);
    };

    const removeFile = (fileName: string) => {
        setFiles(prev => prev.filter(item => item.name !== fileName))
    };

    const getJobDescriptionFromAI = () => {
        const skills = multipleChoiceExamSkills.filter(i => Boolean(i.trim()));
        const multipleLangs = multipleChoiceCodingExamLanguages.filter(i => Boolean(i.trim()));
        const codingLangs = codingExamLanguages.filter(i => i.id !== '-1').map(i => i.id);

        const jobInfo = {
            skills,
            get: true,
            city: city.city,
            state: state.state,
            job_title: jobTitle,
            country: country.title,
            job_type: jobType?.title,
            salary_amount: salaryAmount,
            company: profile?.companyName,
            workplace_type: workplaceType?.title,
            deadline: fromDatePickerFormat(deadline),
            salary_frequency: salaryFrequency?.title,
            programming_languages: [...multipleLangs, ...codingLangs],
        };

        setFetchingJobDescription(true);
        dispatch(getJobDescriptionFromAIThunk(jobInfo))
            .unwrap()
            .then((response: any) => {
                setFetchingJobDescription(false);
                if (response) {
                    const parsed = JSON.parse(response);
                    setJobDescription(parsed?.job_description);
                }
            })
            .catch(() => {
                setFetchingJobDescription(false);
                dispatch(setNotification(['Error on getting information. Try again...', 'error']));
            })
    };

    const submit = (action: 'edit' | 'done') => {
        if (!jobId) return;

        setIsLoading(true);
        const jobInfo = {
            city,
            state,
            country,
            jobType,
            jobTitle,
            workplaceType,
            jobDescription,
            resumeMatching,
            baseSalaryAmount: salaryAmount,
            baseSalaryFrequency: salaryFrequency,
            multipleChoiceExamDeadline: fromDatePickerFormat(deadline),
        };

        dispatch(editJobThunk({
            jobId,
            action,
            jobInfo,
        }))
            .unwrap()
            .then(() => {
                const tab = action === 'done' ? 'Posted' : 'Unposted';
                navigate(`/workspace?tab=${tab}`);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const resetInterviewToDefault = () => {
        if (!rawInterviewQuestions) return;
        const questions = rawInterviewQuestions.map((i) => ({
            id: i.id,
            question: i.title,
            domain: 'general',
            answerTime: '300',
        }));

        setDefaultInterviewQuestions(questions);
        // setSelectedInterviewQuestions(questions);
    };

    return {
        step,
        files,
        setFiles,
        jobTitle,
        posting,
        country,
        state,
        states,
        jobType,
        cities,
        city,
        submit,
        setStep,
        setWorkplaceType,
        setJobTitle,
        setJobDescription,
        setSalaryAmount,
        setJobType,
        setCityAndState,
        fileSelect,
        deadline,
        setDeadline,
        removeFile,
        stepCount,
        resumeMatching,
        setCity,
        setState,
        countries,
        setCountry,
        salaryAmount,
        workplaceType,
        jobDescription,
        salaryFrequency,
        setResumeMatching,
        setSalaryFrequency,
        takingMultipleChoiceExam,
        takingRecruitingInterview,
        codingExamLanguages,
        setTakingMultipleChoiceExam,
        setTakingRecruitingInterview,
        fetchingJobDescription,
        resetInterviewToDefault,
        getJobDescriptionFromAI,
        multipleChoiceCodingExam,
        multipleChoiceExamSkills,
        extractFileTextErrorModal,
        defaultInterviewQuestions,
        selectedInterviewQuestions,
        setMultipleChoiceExamSkills,
        setMultipleChoiceCodingExam,
        setDefaultInterviewQuestions,
        setSelectedInterviewQuestions,
        multipleChoiceCodingExamLanguages,
        setMultipleChoiceCodingExamLanguages,
        submittingJobModal,


        isLoading,
        setIsLoading,
        jobId,
    };
};